// src/api/liveMatchApi.js
// Canlı maç işlemleri için MERKEZİ API katmanı.
//
// Tasarım ilkeleri:
//  - Mevcut paylaşılan axios instance'ı (src/api/api.js) kullanılır.
//    YENİ bir axios instance OLUŞTURULMAZ (kural 11-12).
//  - Mevcut backend endpointleri korunur. Uydurma endpoint kullanılmaz.
//    Canlı maç olayları bugün maç kaydındaki `live_events` alanında (snapshot
//    modeli) tutuluyor; bu katman aynı modeli merkezîleştirir.
//  - Her fonksiyonda: timeout, abort signal desteği, normalize edilmiş hata,
//    normalize edilmiş response.
//  - Geçiş dönemi için (Stage 24) tek noktadan 404-fallback iskeleti bırakıldı.
//    Yeni per-event endpointleri backend'e eklenince yalnızca bu dosya değişir.

import api, { apiGet, clearApiGetCache } from "./api";
import {
  normalizeApiError,
  logApiError,
} from "../utils/normalizeApiError";
import {
  normalizeEvent,
  normalizeMutationResponse,
} from "../services/liveMatchEventNormalizer";
import { buildScopedPath } from "../utils/cityScope";
import { API_CACHE_TTLS, API_TIMEOUTS } from "./apiConfig";

// Varsayılan timeout'lar (ms). Kritik olaylar hızlı timeout ile başarısız
// olup retry mekanizmasına devredilebilsin.
const MUTATION_TIMEOUT = API_TIMEOUTS.LIVE_MUTATION;
const READ_TIMEOUT = API_TIMEOUTS.DEFAULT;

const SNAPSHOT_TTL = 2000;
const SNAPSHOT_CACHE_LIMIT = 24;
const snapshotCache = new Map();
const snapshotInflight = new Map();

const unwrap = (value) => value?.data?.data ?? value?.data ?? value;

const normalizeSnapshot = (value) => {
  const raw = unwrap(value) || {};
  const match = raw.match ?? raw.mac ?? raw.matchData ?? {};
  const timer = raw.timer ?? raw.liveTimer ?? {};
  const lineup = raw.lineup ?? raw.kadro ?? {};
  const events = raw.events ?? raw.olaylar;
  const baseMs = Number(timer.base_ms ?? timer.baseMs);
  const baseAt = Date.parse(timer.base_at ?? timer.baseAt ?? "");
  const running = timer.running ?? timer.isRunning;
  // base_at sunucu saatinde; yerel saat sapmışsa fark doğrudan süreye yansır.
  // Sunucunun aynı yanıtta gönderdiği serverNow ile aynı koordinata taşınır.
  const serverNow = Date.parse(timer.serverNow ?? timer.server_now ?? raw.serverNow ?? "");
  const localNow = Number.isFinite(serverNow) ? serverNow : Date.now();
  const clockMs = Number.isFinite(baseMs)
    ? Math.max(0, baseMs + (running && Number.isFinite(baseAt) ? Math.max(0, localNow - baseAt) : 0))
    : undefined;
  return {
    ...match,
    ...raw,
    ...(lineup && typeof lineup === "object" ? lineup : {}),
    ...(Number.isFinite(clockMs) ? { clockMs, running: Boolean(running), updatedAt: Date.now() } : {}),
    ...(events != null ? { live_events: parseEvents(events) } : {}),
  };
};

const rememberSnapshot = (matchId, data, etag) => {
  const key = String(matchId);
  snapshotCache.delete(key);
  snapshotCache.set(key, { data, etag, storedAt: Date.now() });
  while (snapshotCache.size > SNAPSHOT_CACHE_LIMIT) {
    snapshotCache.delete(snapshotCache.keys().next().value);
  }
  return data;
};

const parseEvents = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value !== "string") return [];
  try { return JSON.parse(value); } catch { return []; }
};

const legacySnapshot = async (matchId, signal) => {
  // The match response already carries live events/lineups on older servers.
  // Only ask dedicated endpoints for fields that are actually absent.
  const matchResponse = await apiGet(`/maclar/${matchId}`, {
    signal, timeout: READ_TIMEOUT, cacheTtl: API_CACHE_TTLS.NONE,
  });
  const match = unwrap(matchResponse) || {};
  const missingTimer = match.timer == null && match.live_timer == null && match.live_time == null;
  const missingLineup = match.live_home_lineup == null && match.home_lineup == null && match.kadro == null;
  const missingEvents = match.live_events == null && match.events == null && match.olaylar == null;
  const requests = [];
  if (missingTimer) requests.push(apiGet(`/maclar/${matchId}/timer`, { signal, timeout: READ_TIMEOUT }).then(r => ["timer", unwrap(r)]));
  if (missingLineup) requests.push(apiGet(`/maclar/${matchId}/kadro`, { signal, timeout: READ_TIMEOUT }).then(r => ["lineup", unwrap(r)]));
  if (missingEvents) requests.push(apiGet(`/api/maclar/${matchId}/olaylar`, { signal, timeout: READ_TIMEOUT }).then(r => ["events", unwrap(r)]));
  const extras = await Promise.all(requests.map(request => request.catch(() => null)));
  const merged = { ...match };
  extras.filter(Boolean).forEach(([kind, value]) => {
    if (kind === "timer") merged.timer = value?.timer || value || {};
    if (kind === "lineup") Object.assign(merged, value?.lineup || value || {});
    if (kind === "events") merged.live_events = parseEvents(value?.events ?? value);
  });
  return merged;
};

/** Socket-first consumers use this single, ETag-aware snapshot reader. */
export const getLiveSnapshot = (matchId, options = {}) => {
  const { signal, force = false, reason = "initial" } = options;
  if (matchId == null || matchId === "") return Promise.reject(new Error("matchId is required"));
  const key = String(matchId);
  const cached = snapshotCache.get(key);
  if (!force && cached && Date.now() - cached.storedAt < SNAPSHOT_TTL) return Promise.resolve(cached.data);
  if (snapshotInflight.has(key)) return snapshotInflight.get(key);

  const request = (async () => {
    try {
      const response = await api.get(`/api/maclar/${matchId}/live-snapshot`, {
        signal,
        timeout: READ_TIMEOUT,
        headers: cached?.etag ? { "If-None-Match": cached.etag } : undefined,
        validateStatus: status => (status >= 200 && status < 300) || status === 304,
        __requestReason: reason,
      });
      if (response.status === 304 && cached) return rememberSnapshot(key, cached.data, cached.etag);
      return rememberSnapshot(key, normalizeSnapshot(response), response.headers?.etag);
    } catch (error) {
      if (signal?.aborted || error?.code === "ERR_CANCELED") throw error;
      const status = error?.response?.status;
      // Eski yol yalnızca uç hiç yoksa (404, eski sunucu) kullanılır. 5xx ya
      // da zaman aşımında sunucu zaten zorlanıyordur; üç ek istek atmak yükü
      // katlayıp kilitlenmeyi büyütüyordu. Elde kopya varsa o gösterilir.
      if (status !== 404) {
        if (cached) return cached.data;
        throw error;
      }
      const data = await legacySnapshot(matchId, signal);
      return rememberSnapshot(key, normalizeSnapshot(data), null);
    }
  })().finally(() => snapshotInflight.delete(key));
  snapshotInflight.set(key, request);
  return request;
};

export const clearLiveSnapshotCache = (matchId) => {
  if (matchId == null) snapshotCache.clear();
  else snapshotCache.delete(String(matchId));
};

// Backend'in kabul ettiği idempotency alanı. Backend camelCase istiyorsa
// clientEventId, snake_case istiyorsa client_event_id kullanılabilsin diye
// her ikisini de payload'a ekliyoruz (fazladan alan zararsızdır).
const withIdempotencyFields = (payload = {}, clientId) => {
  if (!clientId) return { ...payload };
  return {
    ...payload,
    client_event_id: payload.client_event_id ?? clientId,
    clientEventId: payload.clientEventId ?? clientId,
  };
};

const withActionFields = (payload = {}, clientActionId) => {
  if (!clientActionId) return { ...payload };
  return {
    ...payload,
    client_action_id: payload.client_action_id ?? clientActionId,
    clientActionId: payload.clientActionId ?? clientActionId,
  };
};

// Ortak hata sarmalayıcı: normalize eder, dev logu basar, fırlatır.
const run = async (scope, fn) => {
  try {
    return await fn();
  } catch (error) {
    const normalized = normalizeApiError(error);
    logApiError(scope, normalized);
    // normalize edilmiş hatayı fırlat; çağıran retry kararını kolayca versin.
    normalized.__isNormalizedApiError = true;
    throw normalized;
  }
};

/* =========================================================
   READ
========================================================= */

/**
 * Maçın tam durumunu getirir (skor, timer, live_events, lineups...).
 * Canlı veri olduğu için cache KULLANILMAZ.
 */
export const fetchMatchState = (matchId, { signal } = {}) =>
  run("fetchMatchState", async () => {
    const { data } = await apiGet(`/maclar/${matchId}`, {
      signal,
      timeout: READ_TIMEOUT,
      cacheTtl: API_CACHE_TTLS.NONE,
    });
    return data;
  });

/**
 * Maç kadrosunu getirir. Mevcut modelde kadro maç kaydından türetilir.
 */
export const fetchMatchLineup = (matchId, { signal } = {}) =>
  run("fetchMatchLineup", async () => {
    const { data } = await apiGet(`/maclar/${matchId}`, {
      signal,
      timeout: READ_TIMEOUT,
      cacheTtl: API_CACHE_TTLS.NONE,
    });
    return {
      home: data?.live_home_lineup ?? null,
      away: data?.live_away_lineup ?? null,
      homeRoster: data?.live_home_roster ?? data?.home_roster ?? null,
      awayRoster: data?.live_away_roster ?? data?.away_roster ?? null,
      raw: data,
    };
  });

/**
 * Maç olaylarını getirir (normalize edilmiş liste).
 */
export const fetchMatchEvents = (matchId, { signal } = {}) =>
  run("fetchMatchEvents", async () => {
    const { data } = await apiGet(`/maclar/${matchId}`, {
      signal,
      timeout: READ_TIMEOUT,
      cacheTtl: API_CACHE_TTLS.NONE,
    });
    let list = data?.live_events ?? [];
    if (typeof list === "string") {
      try {
        list = JSON.parse(list);
      } catch {
        list = [];
      }
    }
    if (!Array.isArray(list)) list = [];
    return list.map(normalizeEvent).filter(Boolean);
  });

/**
 * Timer durumunu getirir (mevcut PUT/GET timer endpointi).
 */
export const fetchMatchTimer = (matchId, { signal } = {}) =>
  run("fetchMatchTimer", async () => {
    const { data } = await api.get(`/maclar/${matchId}/timer`, {
      signal,
      timeout: READ_TIMEOUT,
    });
    return data;
  });

/* =========================================================
   SCOPED LISTS (cache'li - Stage 11)
========================================================= */

/**
 * Şehre göre scoped maç listesi. apiGet cache/dedupe kullanır.
 */
export const fetchMatches = ({ user, city, signal, cacheTtl } = {}) =>
  run("fetchMatches", async () => {
    const url = buildScopedPath("/maclar", { user, city });
    const { data } = await apiGet(url, {
      signal,
      timeout: READ_TIMEOUT,
      cacheTtl: cacheTtl ?? 0, // maç listesi taze olmalı
    });
    return data;
  });

/**
 * Şehre göre scoped takım listesi. Takımlar uzun süre cache edilebilir.
 */
export const fetchTeams = ({ user, city, signal, cacheTtl } = {}) =>
  run("fetchTeams", async () => {
    const url = buildScopedPath("/takimlar", { user, city });
    const { data } = await apiGet(url, {
      signal,
      timeout: READ_TIMEOUT,
      cacheTtl: cacheTtl ?? 5 * 60 * 1000, // 5 dk
    });
    return data;
  });

/**
 * Oyuncu havuzu. Orta süreli cache (Stage 11).
 */
export const fetchPlayers = ({ signal, cacheTtl } = {}) =>
  run("fetchPlayers", async () => {
    const { data } = await apiGet("/oyuncular/", {
      signal,
      timeout: READ_TIMEOUT,
      cacheTtl: cacheTtl ?? 3 * 60 * 1000, // 3 dk
    });
    return data;
  });

/* =========================================================
   LOW-LEVEL SNAPSHOT PATCH (mevcut model)
========================================================= */

/**
 * Maç kaydını PATCH'ler. Mevcut reji panelinin snapshot kaydetme yolu.
 * `silent` mevcut backend davranışını korur (?silent=1).
 */
export const patchMatch = (matchId, payload, { signal, silent = true } = {}) =>
  run("patchMatch", async () => {
    const url = `/maclar/${matchId}${silent ? "?silent=1" : ""}`;
    const { data } = await api.patch(url, payload, {
      signal,
      timeout: MUTATION_TIMEOUT,
    });
    return normalizeMutationResponse(data);
  });

/* =========================================================
   EVENT MUTATIONS
   Mevcut modelde olaylar maçtaki live_events snapshot'ında tutulur.
   Bu fonksiyonlar snapshot'ı client_event_id ile birlikte kaydeder.
   Yeni per-event endpoint eklenince fallback bloğu devreye alınır.
========================================================= */

// Yeni endpoint denenip 404 dönerse eski (snapshot) yola düşülür (Stage 24).
// Şu an gerçek yeni endpoint bilinmediğinden default olarak KAPALI.
// Backend hazır olduğunda ENDPOINT_CONFIG.eventsBase set edilerek açılır.
const ENDPOINT_CONFIG = {
  eventsBase: null, // ör. (matchId) => `/maclar/${matchId}/olaylar`
  statsRebuild: null, // ör. (matchId) => `/admin/maclar/${matchId}/istatistik-yeniden`
  statsStatus: null, // ör. (matchId) => `/maclar/${matchId}/istatistik-durumu`
};

export const configureLiveMatchEndpoints = (overrides = {}) => {
  Object.assign(ENDPOINT_CONFIG, overrides);
};

const is404 = (normalized) => normalized?.status === 404;

/**
 * Yeni bir maç olayı oluşturur.
 * @param {string|number} matchId
 * @param {object} payload snapshot alanları (first_team_score, live_events...)
 *   + opsiyonel: clientEventId, meta
 * @param {{signal?: AbortSignal, clientEventId?: string}} opts
 */
export const createMatchEvent = (matchId, payload = {}, opts = {}) =>
  run("createMatchEvent", async () => {
    const { signal, clientEventId } = opts;
    const body = withIdempotencyFields(payload, clientEventId);

    // Geçiş dönemi: yeni endpoint yapılandırıldıysa önce onu dene.
    if (typeof ENDPOINT_CONFIG.eventsBase === "function") {
      try {
        const { data } = await api.post(
          ENDPOINT_CONFIG.eventsBase(matchId),
          body,
          { signal, timeout: MUTATION_TIMEOUT }
        );
        return normalizeMutationResponse(data);
      } catch (error) {
        const normalized = normalizeApiError(error);
        // SADECE açık 404'te eski yola düş. Timeout'ta fallback YAPILMAZ
        // (aksi halde duplicate gol riski doğar).
        if (!is404(normalized)) throw error;
      }
    }

    // Mevcut model: snapshot PATCH.
    const url = `/maclar/${matchId}?silent=1`;
    const { data } = await api.patch(url, body, {
      signal,
      timeout: MUTATION_TIMEOUT,
    });
    return normalizeMutationResponse(data);
  });

/**
 * Var olan bir maç olayını günceller.
 */
export const updateMatchEvent = (matchId, eventId, payload = {}, opts = {}) =>
  run("updateMatchEvent", async () => {
    const { signal, clientActionId } = opts;
    const body = withActionFields(payload, clientActionId);

    if (typeof ENDPOINT_CONFIG.eventsBase === "function") {
      try {
        const { data } = await api.patch(
          `${ENDPOINT_CONFIG.eventsBase(matchId)}/${eventId}`,
          body,
          { signal, timeout: MUTATION_TIMEOUT }
        );
        return normalizeMutationResponse(data);
      } catch (error) {
        const normalized = normalizeApiError(error);
        if (!is404(normalized)) throw error;
      }
    }

    const url = `/maclar/${matchId}?silent=1`;
    const { data } = await api.patch(url, body, {
      signal,
      timeout: MUTATION_TIMEOUT,
    });
    return normalizeMutationResponse(data);
  });

/**
 * Bir maç olayını siler.
 */
export const deleteMatchEvent = (matchId, eventId, payload = {}, opts = {}) =>
  run("deleteMatchEvent", async () => {
    const { signal, clientActionId } = opts;
    const body = withActionFields(payload, clientActionId);

    if (typeof ENDPOINT_CONFIG.eventsBase === "function") {
      try {
        const { data } = await api.delete(
          `${ENDPOINT_CONFIG.eventsBase(matchId)}/${eventId}`,
          { signal, timeout: MUTATION_TIMEOUT, data: body }
        );
        return normalizeMutationResponse(data);
      } catch (error) {
        const normalized = normalizeApiError(error);
        if (!is404(normalized)) throw error;
      }
    }

    // Mevcut model: kalan olay listesini snapshot olarak yaz.
    const url = `/maclar/${matchId}?silent=1`;
    const { data } = await api.patch(url, body, {
      signal,
      timeout: MUTATION_TIMEOUT,
    });
    return normalizeMutationResponse(data);
  });

/* =========================================================
   TIMER MUTATIONS
   Timer HER SANİYE yazılmaz. Sadece start/pause/reset/manual/period/finish.
========================================================= */

const patchTimerSnapshot = async (matchId, payload, opts) => {
  const { signal, clientActionId } = opts || {};
  const body = withActionFields(payload, clientActionId);
  const url = `/maclar/${matchId}?silent=1`;
  const { data } = await api.patch(url, body, {
    signal,
    timeout: MUTATION_TIMEOUT,
  });
  return normalizeMutationResponse(data);
};

export const startMatchTimer = (matchId, payload = {}, opts = {}) =>
  run("startMatchTimer", () => patchTimerSnapshot(matchId, payload, opts));

export const pauseMatchTimer = (matchId, payload = {}, opts = {}) =>
  run("pauseMatchTimer", () => patchTimerSnapshot(matchId, payload, opts));

export const resetMatchTimer = (matchId, payload = {}, opts = {}) =>
  run("resetMatchTimer", () => patchTimerSnapshot(matchId, payload, opts));

export const updateMatchPeriod = (matchId, payload = {}, opts = {}) =>
  run("updateMatchPeriod", () => patchTimerSnapshot(matchId, payload, opts));

/* =========================================================
   LINEUP MUTATION
========================================================= */

export const updateMatchLineup = (matchId, payload = {}, opts = {}) =>
  run("updateMatchLineup", async () => {
    const { signal, clientActionId } = opts;
    const body = withActionFields(payload, clientActionId);
    const url = `/maclar/${matchId}?silent=1`;
    const { data } = await api.patch(url, body, {
      signal,
      timeout: MUTATION_TIMEOUT,
    });
    return normalizeMutationResponse(data);
  });

/* =========================================================
   PLAYER STATS (mevcut endpointler korunur)
========================================================= */

export const fetchMatchTeamStats = (matchId, teamId, { signal } = {}) =>
  run("fetchMatchTeamStats", async () => {
    const { data } = await api.get(
      `/oyuncu-istatistikleri/${matchId}/team/${teamId}`,
      { signal, timeout: READ_TIMEOUT }
    );
    return data;
  });

export const deleteMatchStats = (matchId, { signal } = {}) =>
  run("deleteMatchStats", async () => {
    const { data } = await api.delete(
      `/oyuncu-istatistikleri/match/${matchId}`,
      { signal, timeout: MUTATION_TIMEOUT }
    );
    return data;
  });

export const createPlayerStat = (payload, { signal } = {}) =>
  run("createPlayerStat", async () => {
    const { data } = await api.post("/oyuncu-istatistikleri/", payload, {
      signal,
      timeout: MUTATION_TIMEOUT,
    });
    return data;
  });

/* =========================================================
   ADMIN / STATS QUEUE (yalnızca endpoint yapılandırıldıysa) - Stage 20
========================================================= */

/**
 * Ağır istatistik yeniden hesaplamayı tetikler.
 * Endpoint yapılandırılmadıysa güvenli şekilde { available:false } döner.
 */
export const rebuildMatchStats = (matchId, { signal } = {}) =>
  run("rebuildMatchStats", async () => {
    if (typeof ENDPOINT_CONFIG.statsRebuild !== "function") {
      return { available: false };
    }
    const { data } = await api.post(
      ENDPOINT_CONFIG.statsRebuild(matchId),
      {},
      { signal, timeout: MUTATION_TIMEOUT }
    );
    return { available: true, ...(data || {}) };
  });

/**
 * İstatistik hesaplama durumunu getirir (queue/processing/completed/failed).
 * Endpoint yoksa null döner.
 */
export const fetchMatchStatsStatus = (matchId, { signal } = {}) =>
  run("fetchMatchStatsStatus", async () => {
    if (typeof ENDPOINT_CONFIG.statsStatus !== "function") {
      return null;
    }
    const { data } = await api.get(ENDPOINT_CONFIG.statsStatus(matchId), {
      signal,
      timeout: READ_TIMEOUT,
    });
    return data;
  });

export const invalidateMatchListCache = () => {
  clearApiGetCache((key) => key.includes("/maclar"));
};

const liveMatchApi = {
  getLiveSnapshot,
  clearLiveSnapshotCache,
  fetchMatchState,
  fetchMatchLineup,
  fetchMatchEvents,
  fetchMatchTimer,
  fetchMatches,
  fetchTeams,
  fetchPlayers,
  patchMatch,
  createMatchEvent,
  updateMatchEvent,
  deleteMatchEvent,
  startMatchTimer,
  pauseMatchTimer,
  resetMatchTimer,
  updateMatchPeriod,
  updateMatchLineup,
  fetchMatchTeamStats,
  deleteMatchStats,
  createPlayerStat,
  rebuildMatchStats,
  fetchMatchStatsStatus,
  configureLiveMatchEndpoints,
  invalidateMatchListCache,
};

export default liveMatchApi;
